// Turns a picked photo into a small square JPEG data URL, on the device, before it is sent.
// The API caps a request body at 64 KiB, so the result is kept comfortably under ~40 KB.

export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
const SIZE = 192;
const MAX_DATA_URL_CHARS = 40_000;

export class AvatarError extends Error {}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new AvatarError("We couldn't read that image. Try a different photo."));
    };
    image.src = url;
  });
}

export async function fileToAvatarDataUrl(file: File): Promise<string> {
  if (!ACCEPTED.includes(file.type)) throw new AvatarError("Please choose a JPG, PNG or WebP photo.");
  if (file.size > MAX_PHOTO_BYTES) throw new AvatarError("That photo is over 5 MB. Please choose a smaller one.");

  const image = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const context = canvas.getContext("2d");
  if (!context) throw new AvatarError("Your browser can't process photos. You can skip this step.");

  // Centre-crop to a square, then scale down.
  const side = Math.min(image.naturalWidth, image.naturalHeight);
  const sx = (image.naturalWidth - side) / 2;
  const sy = (image.naturalHeight - side) / 2;
  context.drawImage(image, sx, sy, side, side, 0, 0, SIZE, SIZE);

  for (const quality of [0.85, 0.7, 0.55, 0.4]) {
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    if (dataUrl.length <= MAX_DATA_URL_CHARS) return dataUrl;
  }
  throw new AvatarError("That photo is too detailed to shrink. Try a simpler one.");
}
